import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatThousandsSeparator } from 'helpers/others.helper'
import { LearningPath } from 'graphqlAPI'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import { useAccess } from 'contexts/AccessContext'
import { LearningPathsColumns, prefixTable } from './constants'
import LearningPathProgress from './LearningPathProgress'
import Options from './Options'
import styles from './useColumns.module.scss'

type Data = LearningPath

interface useColumnsProps {
  onDownload: (id: string) => void
}

export function useColumns({ onDownload }: useColumnsProps) {
  const { t, i18n } = useTranslation()
  const { access } = useAccess()
  const canDownload = access.canDownloadReportWithViewAccess()

  const prefixHeaders = `${prefixTable}.headers`

  const columns = useMemo(
    () =>
      [
        {
          title: t(`${prefixHeaders}.name`),
          key: LearningPathsColumns.name,
          sorteable: true,
          hideable: false,
          width: 70,
          render: (_: any, { name }: Data) => {
            return <div>{name}</div>
          },
        },
        {
          title: t(`${prefixHeaders}.lmsProfile`),
          key: LearningPathsColumns.lmsProfile,
          width: 170,
          sorteable: true,
          render: (_: any, learningPath: Data) => (
            <ProfilesCountCell
              resourceId={learningPath.id}
              resourceType="learning_path"
              count={learningPath.profiles?.length ?? 0}
              translationKey={`${prefixTable}.columns.profiles`}
            />
          ),
        },
        {
          title: t(`${prefixHeaders}.usersCount`),
          key: LearningPathsColumns.usersCount,
          width: 70,
          sorteable: true,
          render: (_: any, { usersCount }: Data) =>
            formatThousandsSeparator(usersCount ?? 0, i18n.language),
        },
        {
          title: t(`${prefixHeaders}.courses`),
          key: LearningPathsColumns.coursesCount,
          width: 30,
          sorteable: true,
          render: (_, { coursesCount }: Data) => (
            <span>{coursesCount || '---'}</span>
          ),
        },
        {
          title: t(`${prefixHeaders}.progress`),
          key: LearningPathsColumns.progress,
          width: 60,
          sorteable: true,
          render: (_, { id, monitoring }: Data) =>
            (monitoring && (
              <LearningPathProgress
                id={'progress-' + id}
                progress={{
                  finish: monitoring.usersFinish,
                  inProgress: monitoring.usersInProgress,
                  pending: monitoring.usersPending,
                }}
              />
            )) || <div className="text-center">---</div>,
        },
        canDownload && {
          title: '',
          key: 'action',
          width: 45,
          hideable: false,
          align: 'center',
          fixed: 'right',
          className: styles.actionColumn,
          render: (_, data: Data) => (
            <Options onDownload={() => onDownload(data.id)} />
          ),
        },
      ].filter(Boolean),
    [canDownload, i18n.language, onDownload, prefixHeaders, t]
  )
  return columns
}
